import { createFileRoute } from "@tanstack/react-router";
import { SiteShell, PageHero } from "@/components/site/SiteShell";
import { Section } from "@/components/site/Blocks";
import { states, isLicensedState } from "@/data/states";
import { SITE } from "@/data/site";
import { buildHead } from "@/lib/seo-head";
import { LicensingDisclosure } from "@/components/site/LicensingDisclosure";
import { PageEssentials, PageOutro } from "@/components/site/PageEssentials";
import { COMPANY } from "@/config/company";
import { NMLS_CONSUMER_ACCESS_LABEL } from "@/lib/legal-disclosure";
import { EqualHousingLogo } from "@/components/site/EqualHousingLogo";

const URL = "/legal/licensing";

export const Route = createFileRoute("/legal/licensing")({
  head: () =>
    buildHead({
      title: "Licensing & State Disclosures | Simply Approved",
      description:
        "Simply Approved Mortgages is licensed in Florida and Colorado. Business-purpose loans to LLCs and legal entities in additional states where permitted.",
      url: URL,
      keywords: "NMLS licensing, mortgage broker license, Florida Colorado mortgage license, state licensing disclosures, business purpose loan licensing, LLC lending states",
      breadcrumbs: [
        { name: "Home", url: "/" },
        { name: "Legal", url: "/legal" },
        { name: "Licensing", url: URL },
      ],
      localBusiness: false,
    }),
  component: () => (
    <SiteShell>
      <PageHero eyebrow="Legal" title="Licensing & State Disclosures" />
      <PageEssentials />
      <Section>
        <div className="prose prose-slate mx-auto max-w-3xl">
          <LicensingDisclosure className="not-prose rounded-lg border border-border bg-muted/40 p-5 text-sm" />
          <p className="mt-6">Business-purpose loans on non-owner-occupied 1–4 unit residential investment property are generally arranged to an entity borrower (LLC, LP, corporation) where permitted by state law. State-specific notes:</p>

          <ul>
            {states.map((s) => (
              <li key={s.slug}><strong>{s.name} {isLicensedState(s.slug) ? "(Licensed)" : "(LLC business-purpose only)"}:</strong> {s.licensingNote}</li>
            ))}
          </ul>

          <h2>Colorado disclosure</h2>
          <p>Regulated by the Colorado Division of Real Estate. Colorado consumers may check the license status of a mortgage company or mortgage loan originator, and file a written complaint, with the Colorado Division of Real Estate, 1560 Broadway, Suite 925, Denver, CO 80202.</p>

          <h2>Florida disclosure</h2>
          <p>Simply Approved Mortgages LLC is licensed as a mortgage broker by the Florida Office of Financial Regulation (License #MBR7685). Florida consumers may verify licensure or file a complaint with the Florida Office of Financial Regulation, 200 East Gaines Street, Tallahassee, FL 32399.</p>

          <h2>How to verify us or file a complaint</h2>
          <p>Our license record, including every state we are authorized in, is public. Verify it on{" "}
            <a href={COMPANY.nmlsConsumerAccessUrl} target="_blank" rel="noopener noreferrer">{NMLS_CONSUMER_ACCESS_LABEL}</a>.
            You may also contact us directly at {SITE.phone} or through our <a href="/contact">contact form</a>, or submit a complaint to the state regulator where the property or borrower is located.</p>

          <p className="text-sm text-muted-foreground">Office locations are not a representation that we are licensed or authorized to arrange financing in every state. Product availability, eligibility, and terms vary by state, lender, borrower, and property. Equal Housing Opportunity.</p>
          <EqualHousingLogo className="not-prose mt-4 text-foreground" size={36} />
        </div>
      </Section>
    <PageOutro />
      </SiteShell>
  ),
});
